import { t } from "@lingui/core/macro";
import { chunk, compact, isEmpty, map, trim } from "lodash/fp";

type Conjunction = "and" | "or";

const getTranslatedConjunction = (conjunction: Conjunction) => {
  switch (conjunction) {
    case "and":
      return t`and`;
    case "or":
      return t`or`;
    default:
      throw new Error(`unknown conjunction: ${conjunction}`);
  }
};

/**
 * Helper method for creating a comma-separated list w/ Oxford comma and
 * conjunction. The conjunction must be one of the supported values and will
 * be translated automatically.
 * */
export const formattedListWithConjunction = <T extends string>(
  items?: T[],
  conjunction: Conjunction = "and",
) => {
  if (items == null || isEmpty(items)) return "";

  const conjunctionString = getTranslatedConjunction(conjunction);
  if (items.length === 1) return items.toString();
  if (items.length === 2) return items.join(` ${conjunctionString} `);

  const [allButLastItem, lastItem] = chunk(items.length - 1, items);
  return `${allButLastItem.join(", ")}, ${conjunctionString} ${lastItem}`;
};

export const toFormattedStringList = (
  items: string[],
  { bulleted, lineBreak }: { bulleted: boolean; lineBreak?: boolean } = {
    bulleted: true,
    lineBreak: false,
  },
) => {
  if (isEmpty(items)) return "";

  if (bulleted) {
    const [firstItem, ...restOfItems] = items;
    // prepend first list item with bullet
    return [`* ${firstItem}`, ...restOfItems].join("\n* ");
  }

  return items.join(`\n${lineBreak ? "\n" : ""}`);
};

export const splitParagraphs = (summary: string) =>
  compact(map(trim, (summary ?? "").split(/\n/)));

const PUNCTUATION_REGEX = /.*[,.?!;]+$/;
const PUNCTUATION_REPLACE_REGEX = /[,.?!;]+$/;

export const truncateAlongWordBoundary = (
  str: string | null | undefined,
  limit: number,
  { ellipsis }: { ellipsis?: boolean } = { ellipsis: true },
) => {
  if (!str || str.length <= limit) return str ?? "";

  let substring = str.slice(0, limit);
  const endsOnPunctuation = Boolean(substring.match(PUNCTUATION_REGEX));

  if (substring.includes(" ") && !endsOnPunctuation) {
    substring = substring.slice(0, substring.lastIndexOf(" "));
  }

  return `${substring.replace(PUNCTUATION_REPLACE_REGEX, "")}${
    ellipsis ? "..." : ""
  }`;
};
